import i18n from "../utils/i18n";
import { getMultilingualDisplayName } from "../utils/i18nDisplay";
import {
  Competitor,
  competitiorCondition as competitiorConditionType,
  competitorType,
  exchangeType,
  Material,
  materialType,
  PricingAgreementFormFormatType,
  PricingAgreementFormType,
  pricingType,
  quantitySubType,
  quantityType
} from "./types";

// アプリケーションバージョン
export const CEAS_APP_VERSION = "3.3";

// Googleドライブ共有フォルダ名
export const SharedDriveName = "EXT-CEAS";

// 日本円通貨名称
export const CurrencyNameJPY = "JPY";

/** サブウィンドウ名：新規投資申請画面 */
export const SubWindowInvestmentScreeningDetailName = "SubWindowInvestmentScreeningDetail";

/** 別タブ表示名：検索系画面からCtrl+クリック等で開く独立した編集可能画面（サブウィンドウとは別役割） */
export const NewTabInvestmentScreeningDetailName = "NewTabInvestmentScreeningDetail";

// 年月日入力可能範囲
export const ValidDateRangeStart = new Date("2000-01-01");
export const ValidDateRangeEnd = new Date("2100-12-31");

// 見積チェックリスト テキスト入力項目の文字数上限
export const EstimateCheckListTextMaxLength = 1000;

/**
 * 入力項目の文字数上限（バックエンドの入力検証と対応する値）
 *
 * バックエンドは入力検証でこれを超える値を拒否する。画面側の `maxLength` と検証メッセージには必ずこの定数を使い、
 * リテラルを直接書かないこと。値を変更する場合は、対応するバックエンドも同時に修正する必要がある。
 *   - 「utils/Constants.mjs」の InputLimits に同名で定義があるもの
 *   - それ以外は、コメントに記載したバックエンドの Lambda に実装されている
 */
export const InputLimits = {
  /** マニュアル名・操作説明動画名・テンプレート名（backend: InputLimits.MANUAL_NAME） */
  MANUAL_NAME: 255,
  /** 外部リンクURL。https:// で始まり、空白・制御文字を含まないこと（backend: InputLimits.URL） */
  URL: 2048,
  /** お知らせのタイトル（言語ごと）（backend: InputLimits.NOTIFICATION_TITLE） */
  NOTIFICATION_TITLE: 200,
  /** お知らせの本文（言語ごと）（backend: InputLimits.NOTIFICATION_BODY） */
  NOTIFICATION_BODY: 5000,
  /** マスタの説明欄（設備区分の説明）（backend: InputLimits.DESCRIPTION） */
  DESCRIPTION: 500,
  /** 国籍ID（フロントのみの制限。backend は InputLimits.ID の255まで許可） */
  NATIONALITY_ID: 10,
  /** 国籍名（日本語・英語）（backend: InputLimits.NATIONALITY_NAME） */
  NATIONALITY_NAME: 500,
  /** 会社/工場ID（フロントのみの制限。backend は InputLimits.ID の255まで許可） */
  COMPANY_PLANT_ID: 50,
  /** 会社/工場名（日本語・英語）（backend: InputLimits.COMPANY_PLANT_NAME） */
  COMPANY_PLANT_NAME: 200,
  /** 会社コード（backend: InputLimits.COMPANY_CODE） */
  COMPANY_CODE: 20,
  /** 所在国（backend: InputLimits.LOCATION_COUNTRY） */
  LOCATION_COUNTRY: 100,
  /** 金利マスタの地域コード（フロントのみの制限。backend は InputLimits.ID の255まで許可） */
  AREA_CODE: 50,
  /** 金利マスタの地域名（言語ごと）（backend: InputLimits.AREA_NAME） */
  AREA_NAME: 100,
  /** 設備区分・設備グループ・設備仕入先の名称（言語ごと）（backend: putEquipmentCategory 等の各 Lambda に100で実装） */
  EQUIPMENT_MASTER_NAME: 100,
  /** 経理組織ID・親組織ID（backend: putAccountingOrganization / Batch に50で実装） */
  ACCOUNTING_ORG_ID: 50,
  /** 経理組織名（backend: putAccountingOrganization / Batch に100で実装） */
  ACCOUNTING_ORG_NAME: 100,
  /** 経理組織名（英語）（backend: putAccountingOrganization / Batch に200で実装） */
  ACCOUNTING_ORG_NAME_EN: 200,
  /** 承認コメント（backend: InputLimits.APPROVAL_COMMENT） */
  APPROVAL_COMMENT: 100,
  /** 議長名（backend: InputLimits.CHAIR_NAME） */
  CHAIR_NAME: 50,
  /** 問い合わせメールの本文（backend: sendInquiryMail に2000で実装） */
  INQUIRY_BODY: 2000,
} as const;

// 営業利益率グラフY軸範囲（%）
export const ProfitRateChartYAxisMin = -10;
export const ProfitRateChartYAxisMax = 60;

/** ブックマーク登録可能件数の上限 */
export const BookmarkMaxCount = 100;

// アプリケーションパス情報
export const ApplicationPathName = {
  // ログイン画面
  SignIn: { Url: '/signin', get Title() { return i18n.t('common.constants.applicationPathName.signIn', { defaultValue: 'ログイン' }); } },

  // ホーム画面
  Home: { Url: '/home', get Title() { return i18n.t('common.constants.applicationPathName.home', { defaultValue: 'ホーム' }); } },

  // FAQ画面
  Faq: { Url: '/faq', get Title() { return i18n.t('common.constants.applicationPathName.faq', { defaultValue: 'FAQ' }); } },

  // 入力対象検索画面
  InputList: { Url: '/input-list', get Title() { return i18n.t('common.constants.applicationPathName.inputList', { defaultValue: '入力対象検索' }); } },
  // 承認対象検索画面
  ApproveList: { Url: '/approve-list', get Title() { return i18n.t('common.constants.applicationPathName.approveList', { defaultValue: '承認対象検索' }); } },
  // 投資・振り返り検索画面
  InvestmentList: { Url: '/investment-list', get Title() { return i18n.t('common.constants.applicationPathName.investmentList', { defaultValue: '投資・振り返り検索' }); } },
  // 事業管理一覧画面
  ManagementList: { Url: '/management-list', get Title() { return i18n.t('common.constants.applicationPathName.managementList', { defaultValue: '事業管理一覧' }); } },
  // 投資申請詳細画面
  InvestmentScreeningDetail: { Url: '/investment-screening-detail', get Title() { return i18n.t('common.constants.applicationPathName.investmentScreeningDetail', { defaultValue: '投資申請詳細（仮）' }); } },
  // 状況報告（検収管理）画面
  StatusReport: { Url: '/ceas-status-report', get Title() { return i18n.t('common.constants.applicationPathName.statusReport', { defaultValue: '状況報告（検収管理）' }); } },

  // マスタメンテナンス - 設備分類
  EquipmentCategory: { Url: '/equipment-category', get Title() { return i18n.t('common.constants.applicationPathName.equipmentCategory', { defaultValue: '設備分類' }); } },
  // マスタメンテナンス - 主要装置・機械
  EquipmentGroup: { Url: '/equipment-group', get Title() { return i18n.t('common.constants.applicationPathName.equipmentGroup', { defaultValue: '主要装置・機械' }); } },
  // マスタメンテナンス - サプライヤー
  EquipmentSupplier: { Url: '/equipment-supplier', get Title() { return i18n.t('common.constants.applicationPathName.equipmentSupplier', { defaultValue: 'サプライヤー' }); } },
  // マスタメンテナンス - 金利設定
  InterestRateMaster: { Url: '/interest-rate-master', get Title() { return i18n.t('common.constants.applicationPathName.interestRateMaster', { defaultValue: '金利設定' }); } },
  // マスタメンテナンス - 会社/工場設定
  CompanyPlant: { Url: '/company-plant', get Title() { return i18n.t('common.constants.applicationPathName.companyPlant', { defaultValue: '会社/工場設定' }); } },
  // マスタメンテナンス - 顧客国籍設定
  Nationality: { Url: '/nationality', get Title() { return i18n.t('common.constants.applicationPathName.nationality', { defaultValue: '顧客国籍設定' }); } },
  // マスタメンテナンス - お知らせ管理
  Notification: { Url: '/notification', get Title() { return i18n.t('common.constants.applicationPathName.notification', { defaultValue: 'お知らせ管理' }); } },
  // マスタメンテナンス - 管理体系設定
  AccountingOrganization: { Url: '/accounting-organization', get Title() { return i18n.t('common.constants.applicationPathName.accountingOrganization', { defaultValue: '管理体系設定' }); } },
  // マスタメンテナンス - システム利用設定
  SystemAccessSetting: { Url: '/system-access-setting', get Title() { return i18n.t('common.constants.applicationPathName.systemAccessSetting', { defaultValue: 'システム利用設定' }); } },
  // マスタメンテナンス - 承認確認依頼者設定
  ApprovalRequesterMaster: { Url: '/approval-requester-master', get Title() { return i18n.t('common.constants.applicationPathName.approvalRequesterMaster', { defaultValue: '承認確認依頼者設定' }); } },
  // マスタメンテナンス - 生成AI制限事業部設定
  AIRestrictedOrganization: { Url: '/ai-restricted-organization', get Title() { return i18n.t('common.constants.applicationPathName.aiRestrictedOrganization', { defaultValue: '生成AI制限事業部設定' }); } },
  // マスタメンテナンス - アクティブユーザー確認
  ActiveUsers: { Url: '/login-history', get Title() { return i18n.t('common.constants.applicationPathName.activeUsers', { defaultValue: 'アクティブユーザー確認' }); } },
  // マスタメンテナンス - お問い合わせ設定
  InquireSetting: { Url: '/inquire-setting', get Title() { return i18n.t('common.constants.applicationPathName.inquireSetting', { defaultValue: 'お問い合わせ設定' }); } },
  // マスタメンテナンス - １０か条チェックリスト設定
  CheckListMaster: { Url: '/check-list-master', get Title() { return i18n.t('common.constants.applicationPathName.checkListMaster', { defaultValue: '１０か条チェックリスト設定' }); } },
} as const;

// フロー区分
export const FlowKb = {
  /** 確認 */
  Confirmation: "0",
  /** 承認 */
  Approval: "1",
} as const;

/** メッセージ */
export const Messages = {
  /** 排他制御エラーメッセージ */
  get expiredMessage(): string { return i18n.t('common.message.expiredMessage', { defaultValue: '表示している内容が古いようです。\n画面を再表示して、最初から操作をやり直してください。' }); }
}

// 承認ステータス
export const ApproveStatus = {
  // 申請中
  Pending: { code: "1", name: "申請中" },
  // 製品統括承認待ち
  ProductDirector: { code: "2", name: "製品統括承認待ち" },
  // 事業部長承認待ち
  BusinessManager: { code: "3", name: "事業部長承認待ち" },
  // CC・資材担当確認中（並列ブロック1）
  CCMaterialCheck: { code: "4", name: "CC・資材担当確認中" },
  // 本部長承認中（並列ブロック2）
  HQApproval: { code: "5", name: "本部長承認中" },
  // プレ検討会待ち
  PreReview: { code: "6", name: "プレ検討会待ち" },
  // 本部長設備投資検討会待ち
  HQReview: { code: "7", name: "本部長設備投資検討会待ち" },
  // 会長/副会長/社長 確認待ち
  PresidentReport: { code: "8", name: "会長/副会長/社長 確認待ち" },
  // 承認完了
  ApprovalComplete: { code: "90", name: "承認完了" },
  // 差戻し
  Remand: { code: "97", name: "差戻し" },
  // 却下
  Reject: { code: "98", name: "却下" },
  // 取消し
  Cancel: { code: "99", name: "取消し" },

} as const;

// 承認結果
export const ApproveResult = {
  // 承認
  Approval: { code: "1", name: "承認" },
  // 差戻
  Remand: { code: "97", name: "差戻" },
  // 却下
  Reject: { code: "98", name: "却下" },
  // 取消
  Cancel: { code: "99", name: "取消" },
} as const;


// ユーザー権限（r_ceas_user_permissions.user_role）
export const UserRole = {
  // 一般権限
  General: { code: "1", name: "一般権限" },
  // 事業閲覧権限
  ManagementAdmin: { code: "2", name: "事業閲覧権限" },
  // 全社閲覧権限
  CorporateAdmin: { code: "3", name: "全社閲覧権限" },
  // 指定組織閲覧権限
  DesignatedOrgView: { code: "4", name: "指定組織閲覧権限" },
} as const;

/** UserRole コード値から名称を取得 */
export const getUserRoleNameByCode = (code: string | null | undefined): string => {
  if (!code) return "";
  const role = Object.values(UserRole).find(r => r.code === code);
  if (!role) return "";
  return i18n.t(`common.constants.userRole.${code}`, { defaultValue: role.name });
};

/**
 * S3ファイルのマルウェアスキャン状態（GuardDuty Malware Protection for S3）
 * バックエンドの `utils/malwareScan.mjs` の ScanStatus と対応（変更時は両方修正が必要）
 */
export const ScanStatus = {
  /** 脅威なし・ダウンロード可 */
  Clean: "NO_THREATS_FOUND",
  /** マルウェア検知・ダウンロード不可 */
  Threat: "THREATS_FOUND",
  /** スキャン中 */
  Scanning: "SCANNING",
  /** スキャン失敗 */
  Failed: "FAILED",
  /** スキャン対象外（サイズ上限超過等） */
  Unsupported: "UNSUPPORTED",
  /** タグ参照権限なし */
  AccessDenied: "ACCESS_DENIED",
  /** 状態不明 */
  Unknown: "UNKNOWN",
  /** GuardDuty導入前からある既存ファイル */
  Legacy: "LEGACY",
  /** マルウェアスキャン機能自体が無効（開発環境等、GuardDuty未設定） */
  Disabled: "SCAN_DISABLED",
} as const;

/** マルウェア検知によりダウンロードを遮断すべきか（バックエンドの isBlocked と同一判定） */
export const isScanBlocked = (scanStatus: string | null | undefined): boolean =>
  scanStatus === ScanStatus.Threat;

// ワークフローID（変更する場合はバックエンドも同様に変更が必要）
export const WorkflowId = {
  // 基本情報
  BasicInfo: "1",
  // 数量・単価
  QuantityAndPrice: "2",
  // 顧客取り決め事項
  PricingAgreementForm: "3",
  // スケジュール
  Scheduling: "4",
  // 材料費
  MaterialCost: "5",
  // 製造工程
  ProductionProcess: "6",
  // 設備投資
  Equipment: "7",
  // 費用(P&L)
  ProfitAndLoss: "8",
  // 対象PJの損益試算
  ProjectProfitablityStatement: "9",
  // Simulation（新製品製造）
  SimulationNewProduct: "10",
  // Simulation（生産性向上）
  SimulationProductivityImprovement: "11",
  // 申請書
  ReportAndDraft: "12",
  /** 営業視点 */
  ReviewSales: "13",
  /** 事業（製造・財務）視点 */
  ReviewBusiness: "14",
  /** 今後の見込み */
  ReviewFuture: "15",

  // --- 承認ステージ（直列前半: 20番台） ---
  // 製品統括
  ProductDirector: "20",
  // 事業部長
  BusinessManager: "21",
  // --- 並列ブロック1: CC・資材担当系（30番台） ---
  // クロスチェック（CC）
  CrossCheck: "30",
  // 資材担当役員確認依頼者
  MaterialOfficerRequester: "31",
  // 資材担当役員確認
  MaterialOfficer: "32",
  // --- 並列ブロック2: 本部長承認系（40番台） ---
  // 営業本部長確認依頼者
  SalesHQManagerRequester: "40",
  // 営業本部長承認
  SalesHQManager: "41",
  // 事業本部長確認依頼者（将来用）
  BusinessHQManagerRequester: "42",
  // 事業本部長承認
  BusinessHQManager: "43",
  // --- 直列後半（50番台） ---
  // プレ検討会
  PreReview: "50",
  // 本部長検討会
  HQReview: "51",
  // 会長/副会長/社長 確認
  PresidentReport: "52",
  // 承認完了
  ApproveComplete: "90",
  // 差戻
  Remand: "97",
  // 起案取消し
  Cancel: "99",
} as const;

// 承認ステージごとの操作可能ステータス（バックエンドと同一定義）
export const ValidPreviousStatus: Record<string, string[]> = {
  [WorkflowId.ProductDirector]: [ApproveStatus.ProductDirector.code],    // 製品統括 ← 製品統括承認待ち
  [WorkflowId.BusinessManager]: [ApproveStatus.BusinessManager.code],     // 事業部長 ← 事業部長承認待ち
  // 並列ブロック1: CC・資材担当確認中
  [WorkflowId.CrossCheck]: [ApproveStatus.CCMaterialCheck.code],     // CC ← CC・資材担当確認中
  [WorkflowId.MaterialOfficerRequester]: [ApproveStatus.CCMaterialCheck.code],     // 資材担当依頼者 ← CC・資材担当確認中
  [WorkflowId.MaterialOfficer]: [ApproveStatus.CCMaterialCheck.code],     // 資材担当確認 ← CC・資材担当確認中（依頼者承認後）
  // 並列ブロック2: 本部長承認中
  [WorkflowId.SalesHQManagerRequester]: [ApproveStatus.HQApproval.code],          // 営業本部長依頼者 ← 本部長承認中
  [WorkflowId.SalesHQManager]: [ApproveStatus.HQApproval.code],          // 営業本部長承認 ← 本部長承認中（依頼者承認後）
  [WorkflowId.BusinessHQManagerRequester]: [ApproveStatus.HQApproval.code],          // 事業本部長依頼者 ← 本部長承認中
  [WorkflowId.BusinessHQManager]: [ApproveStatus.HQApproval.code],          // 事業本部長承認 ← 本部長承認中（依頼者承認後）
  // 直列後半
  [WorkflowId.PreReview]: [ApproveStatus.PreReview.code],           // プレ検討会 ← プレ検討会待ち
  [WorkflowId.HQReview]: [ApproveStatus.HQReview.code],            // 本部長検討会 ← 本部長検討会待ち
  [WorkflowId.PresidentReport]: [ApproveStatus.PresidentReport.code],     // 会長/副会長/社長 確認 ← 会長確認待ち
};

/**
 * 確認部署
 */
export const ComfirmDivisions = {
  /** 事業部 */
  companyDivision: "company",
  /** 事業本部 */
  companyHeadDivision: "companyHead",
  /** 営業 */
  salesDivision: "sales",
  /** 調達 */
  procurementDivision: "procurement",
}

/** 確認部署と名称のマッピング（i18n対応） */
export const getDivisionConfirmName = (key: string): string => {
  return i18n.t(`common.constants.divisionConfirm.${key}`, { defaultValue: key });
};
/** 確認部署と名称のマッピング（後方互換） */
export const DivisionConfirmKb: Record<string, string> = {
  company: "事業部確認",
  companyHead: "事業管理確認",
  sales: "営業担当者確認",
  procurement: "調達担当者確認",
};

/** 顧客業界 */
export const CustomerIndustry = {
  /** 車載 */
  InVehicle: { code: "1", name: "車載" },
  /** スマートフォン */
  Smartphone: { code: "2", name: "スマートフォン" },
}

/** 顧客国籍 */
export const CustomerNationality = {
  /** 日本 */
  Japan: { code: "JPN", name: "日本" },
  /** タイ */
  Thailand: { code: "THA", name: "タイ" },
  /** 米国 */
  UnitedStates: { code: "USA", name: "米国" },
}

/** 目的 */
export const PurposeType = {
  /** 新製品 */
  NewProduct: { code: "1", name: "新製品" },
  /** 増産 */
  IncreasedProduction: { code: "2", name: "増産" },
  /** 合理化 */
  Rationalization: { code: "3", name: "合理化" },
  /** 更新・その他 */
  UpdateAndOther: { code: "4", name: "更新・その他" },
}

/** 申請書様式（合理化・更新案件で詳細/簡易を切り替え） */
export const ApplicationFormType = {
  /** 詳細（標準フロー） */
  Detail: { code: "detail", name: i18n.t('common.constants.applicationFormType.detail') },
  /** 簡易（販売数量/費用入力スキップ） */
  Simple: { code: "simple", name: i18n.t('common.constants.applicationFormType.simple') },
}

/** 申請書様式が「簡易」かを判定（合理化/更新案件のみ簡易が成立） */
export const isSimpleApplicationForm = (purpose?: string | null, applicationFormType?: string | null): boolean => {
  if (applicationFormType !== ApplicationFormType.Simple.code) return false;
  return purpose === PurposeType.Rationalization.code || purpose === PurposeType.UpdateAndOther.code;
}

/** 損益表示（簡易様式のときのみ選択可。「表示しない」で損益試算・シミュレーションの表/グラフを非表示） */
export const ProfitLossDisplayType = {
  /** 表示する（既定） */
  Show: { code: "show", get name() { return i18n.t('common.constants.profitLossDisplayType.show') } },
  /** 表示しない */
  Hide: { code: "hide", get name() { return i18n.t('common.constants.profitLossDisplayType.hide') } },
}

/**
 * 損益試算・シミュレーションの表/グラフを非表示にするかを判定
 * 簡易様式（合理化/更新案件）で「表示しない」が選択されている場合のみ true
 */
export const isProfitLossHidden = (
  purpose?: string | null,
  applicationFormType?: string | null,
  profitLossDisplayType?: string | null,
): boolean => {
  if (!isSimpleApplicationForm(purpose, applicationFormType)) return false;
  return profitLossDisplayType === ProfitLossDisplayType.Hide.code;
}

/** 画面閲覧制限で制限できる画面（WorkflowId）。BE の VIEW_RESTRICTABLE_SCREEN_IDS と対応（変更時は両方修正が必要） */
export const ViewRestrictableScreenIds: string[] = [
  WorkflowId.ProductionProcess,
  WorkflowId.Equipment,
  WorkflowId.ProfitAndLoss,
  WorkflowId.ProjectProfitablityStatement,
  WorkflowId.SimulationNewProduct,
  WorkflowId.SimulationProductivityImprovement,
  WorkflowId.ReviewBusiness,
];

/** 設備区分 */
export const EquipmentCategory = {
  /** 土地 */
  Land: { code: "1", name: "土地" },
  /** 建物 */
  Building: { code: "2", name: "建物" },
  /** 構築物 */
  Structure: { code: "3", name: "構築物" },
  /** 機械装置 */
  Machinery: { code: "4", name: "機械装置" },
  /** 車両運搬具 */
  Vehicle: { code: "5", name: "車両運搬具" },
  /** 工具器具備品 */
  ToolsAndEquipment: { code: "6", name: "工具器具備品" },
  /** 金型 */
  Mold: { code: "7", name: "金型" },
  /** その他（事務機器など） */
  Other: { code: "8", name: "その他（事務機器など）" },
  /** ソフトウェア */
  Software: { code: "9", name: "ソフトウェア" },
}

/** 設備用途区分 */
export const EquipmentUsageType = {
  /** 専用設備 */
  Dedicated: { code: "1", get name() { return i18n.t('common.constants.equipmentUsageType.1', { defaultValue: '専用設備' }); } },
  /** 汎用設備 */
  GeneralPurpose: { code: "2", get name() { return i18n.t('common.constants.equipmentUsageType.2', { defaultValue: '汎用設備' }); } },
}

export const PlannedBudgetType = {
  /** 予算内 */
  WithinBudget: { code: "1", get name() { return i18n.t('common.constants.plannedBudgetType.1', { defaultValue: '予算内' }); } },
  /** 予算外 */
  OutsideBudget: { code: "2", get name() { return i18n.t('common.constants.plannedBudgetType.2', { defaultValue: '予算外' }); } },
}

/** １０か条チェックリスト - 該当 */
export const Applicable = {
  /** 未選択 */
  NotSelected: { code: "0", name: "" },
  /** 該当あり */
  Applicable: { code: "1", name: "〇" },
  /** 該当なし */
  NA: { code: "2", name: "N/A" },
}

/** 顧客取り決め事項カテゴリ名 */
export const PricingAgreementCategory = {
  /** 単価の取り決め */
  get pricing() { return i18n.t('common.constants.pricingAgreementCategory.pricing', { defaultValue: '単価の取り決め' }); },
  /** 為替変動の取り決め */
  get exchange() { return i18n.t('common.constants.pricingAgreementCategory.exchange', { defaultValue: '為替変動の取り決め' }); },
  /** 材料費変動の取り決め */
  get material() { return i18n.t('common.constants.pricingAgreementCategory.material', { defaultValue: '材料費変動の取り決め' }); },
  /** 数量の取り決め */
  get quantity() { return i18n.t('common.constants.pricingAgreementCategory.quantity', { defaultValue: '数量の取り決め' }); },
  /** 数量未達時の取り決め */
  get quantityNotReached() { return i18n.t('common.constants.pricingAgreementCategory.quantityNotReached', { defaultValue: '数量未達時の取り決め' }); },
  /** 競合の有無 */
  get competitor() { return i18n.t('common.constants.pricingAgreementCategory.competitor', { defaultValue: '競合の有無' }); },
  /** 競合会社選択 */
  get selectedCompetitors() { return i18n.t('common.constants.pricingAgreementCategory.selectedCompetitors', { defaultValue: '競合会社選択' }); },
  /** 競合状態 */
  get competitiorCondition() { return i18n.t('common.constants.pricingAgreementCategory.competitiorCondition', { defaultValue: '競合状態' }); },
  /** 当社及び競合のシェア */
  get shareOfCompetitorAndCompany() { return i18n.t('common.constants.pricingAgreementCategory.shareOfCompetitorAndCompany', { defaultValue: '当社及び競合のシェア' }); },
  /** ファイルアップロードの有無 */
  get uploadedFile() { return i18n.t('common.constants.pricingAgreementCategory.uploadedFile', { defaultValue: 'アップロード済みファイル' }); },
}

/**
 * 勤務体制
 */
export const ShiftSystem = {
  /** 稼働無し */
  NoOperation: { code: "0", get name() { return i18n.t('common.constants.shiftSystem.0', { defaultValue: '稼働無し' }); } },
  /** １直 */
  OneShiftSystem: { code: "1", get name() { return i18n.t('common.constants.shiftSystem.1', { defaultValue: '１直' }); } },
  /** ２直 */
  TwoShiftSystem: { code: "2", get name() { return i18n.t('common.constants.shiftSystem.2', { defaultValue: '２直' }); } },
  /** ３直 */
  ThreeShiftSystem: { code: "3", get name() { return i18n.t('common.constants.shiftSystem.3', { defaultValue: '３直' }); } },
}

/**
 * 非稼働項目
 */
export const NonWorkingItems = {
  MechanicalTrouble: { code: "1", get name() { return i18n.t('common.constants.nonWorkingItems.1', { defaultValue: '機械トラブル' }); } },
  WaitingForParts: { code: "2", get name() { return i18n.t('common.constants.nonWorkingItems.2', { defaultValue: '部品待ち' }); } },
  Setup: { code: "3", get name() { return i18n.t('common.constants.nonWorkingItems.3', { defaultValue: 'セットアップ' }); } },
  Maintenance: { code: "4", get name() { return i18n.t('common.constants.nonWorkingItems.4', { defaultValue: '保守・メンテナンス' }); } },
  Cleaning: { code: "5", get name() { return i18n.t('common.constants.nonWorkingItems.5', { defaultValue: '清掃' }); } },
  DimensionAdjustment: { code: "6", get name() { return i18n.t('common.constants.nonWorkingItems.6', { defaultValue: '寸法調整' }); } },
  WaitingForQualityControl: { code: "7", get name() { return i18n.t('common.constants.nonWorkingItems.7', { defaultValue: '品管待ち' }); } },
  WaitingForFA: { code: "8", get name() { return i18n.t('common.constants.nonWorkingItems.8', { defaultValue: 'FA待ち' }); } },
  Other: { code: "9", get name() { return i18n.t('common.constants.nonWorkingItems.9', { defaultValue: 'その他' }); } },
  BreakTime: { code: "10", get name() { return i18n.t('common.constants.nonWorkingItems.10', { defaultValue: '休憩時間' }); } },
  PlannedStoppage: { code: "11", get name() { return i18n.t('common.constants.nonWorkingItems.11', { defaultValue: '計画停止' }); } },
}

/**
 * 非稼働項目の画面表示順（保存用コードとは独立して定義。DB保存済みのコード値（1〜9）は不変のため、
 * 新規項目（休憩時間・計画停止）はコード上は末尾（10・11）に追加し、画面上の並び順のみこの配列で制御する）
 */
export const NonWorkingItemsDisplayOrder: string[] = [
  NonWorkingItems.MechanicalTrouble.code,
  NonWorkingItems.WaitingForParts.code,
  NonWorkingItems.BreakTime.code,
  NonWorkingItems.Setup.code,
  NonWorkingItems.Maintenance.code,
  NonWorkingItems.Cleaning.code,
  NonWorkingItems.DimensionAdjustment.code,
  NonWorkingItems.WaitingForQualityControl.code,
  NonWorkingItems.WaitingForFA.code,
  NonWorkingItems.PlannedStoppage.code,
  NonWorkingItems.Other.code,
];

/**
 * サイクルタイム単位
 */
export const CycleTimeUnits = {
  /** 日本 */
  Minutes: { code: "minutes", get name() { return i18n.t('common.constants.cycleTimeUnits.minutes', { defaultValue: '分' }); } },
  /** タイ */
  Seconds: { code: "seconds", get name() { return i18n.t('common.constants.cycleTimeUnits.seconds', { defaultValue: '秒' }); } },
}

/**
 * M_CEAS_SETTING.SettingKey
 */
export const SettingKey = {
  // 営業管理率（営業本社管理費）
  salesManagementRate: "salesManagementRate",
  // 割引率（加重平均資本コスト）
  discountRate: "discountRate",
}

/**
 * M_CEAS_DOWNSIDE_INDEX
 * id           ：項目ID(内部で取り扱う用)
 * downsideId   ：M_CEAS_DOWNSIDE_INDEXのitemIdと紐づくid
 */
export const DownsideItems = {
  // 数量経時変化
  quantityChangeOverTime: { id: "01", downsideId: "01", get name() { return i18n.t('common.constants.downsideItems.quantityChangeOverTime', { defaultValue: '数量経時変化' }); } },
  // 数量上下変動
  quantityFluctuation: { id: "02", downsideId: "02", get name() { return i18n.t('common.constants.downsideItems.quantityFluctuation', { defaultValue: '数量上下変動' }); } },
  // 単価経時変化
  priceChangeOverTime: { id: "03", downsideId: "03", get name() { return i18n.t('common.constants.downsideItems.priceChangeOverTime', { defaultValue: '単価経時変化' }); } },
  // 顧客製品寿命
  customerProductLifecycle: { id: "04", downsideId: "04", get name() { return i18n.t('common.constants.downsideItems.customerProductLifecycle', { defaultValue: '顧客製品寿命' }); } },
  // 為替変動（単価）
  exchangeRateFluctuationToPrice: { id: "05", downsideId: "05", get name() { return i18n.t('common.constants.downsideItems.exchangeRateFluctuationToPrice', { defaultValue: '為替変動（単価）' }); } },
  // 為替変動（原価）
  exchangeRateFluctuationToCost: { id: "06", downsideId: "06", get name() { return i18n.t('common.constants.downsideItems.exchangeRateFluctuationToCost', { defaultValue: '為替変動（原価）' }); } },
  // 材料高騰
  materialCostIncrease: { id: "07", downsideId: "07", get name() { return i18n.t('common.constants.downsideItems.materialCostIncrease', { defaultValue: '材料高騰' }); } },
  // 労務費高騰
  laborCostIncrease: { id: "08", downsideId: "08", get name() { return i18n.t('common.constants.downsideItems.laborCostIncrease', { defaultValue: '労務費高騰' }); } },
  // 競合状態（数量）（1番手）
  competitorStatus1stToQuantity: { id: "09", downsideId: "09", get name() { return i18n.t('common.constants.downsideItems.competitorStatus1stToQuantity', { defaultValue: '競合状態（数量）（1番手）' }); } },
  // 競合状態（単価）（1番手）
  competitorStatus1stToPrice: { id: "10", downsideId: "10", get name() { return i18n.t('common.constants.downsideItems.competitorStatus1stToPrice', { defaultValue: '競合状態（単価）（1番手）' }); } },
  // 競合状態（数量）（2番手）
  competitorStatus2ndToQuantity: { id: "09", downsideId: "11", get name() { return i18n.t('common.constants.downsideItems.competitorStatus2ndToQuantity', { defaultValue: '競合状態（数量）（2番手）' }); } },
  // 競合状態（単価）（2番手）
  competitorStatus2ndToPrice: { id: "10", downsideId: "12", get name() { return i18n.t('common.constants.downsideItems.competitorStatus2ndToPrice', { defaultValue: '競合状態（単価）（2番手）' }); } },
  // 競合状態（数量）（競合なし）
  competitorStatusNoToQuantity: { id: "09", downsideId: "13", get name() { return i18n.t('common.constants.downsideItems.competitorStatusNoToQuantity', { defaultValue: '競合状態（数量）（競合なし）' }); } },
  // 競合状態（単価）（競合なし）
  competitorStatusNoToToPrice: { id: "10", downsideId: "14", get name() { return i18n.t('common.constants.downsideItems.competitorStatusNoToToPrice', { defaultValue: '競合状態（単価）（競合なし）' }); } }
}

/**
 * ダウンサイト指数項目
 * id  ：DownsideItemsの項目IDと紐づくID
 */
export const DownsideItemsTitle = {
  // 数量経時変化
  quantityChangeOverTime: { id: "01" },
  // 数量上下変動
  quantityFluctuation: { id: "02" },
  // 単価経時変化
  priceChangeOverTime: { id: "03" },
  // 顧客製品寿命
  customerProductLifecycle: { id: "04" },
  // 為替変動（単価）
  exchangeRateFluctuationToPrice: { id: "05" },
  // 為替変動（原価）
  exchangeRateFluctuationToCost: { id: "06" },
  // 材料高騰
  materialCostIncrease: { id: "07" },
  // 労務費高騰
  laborCostIncrease: { id: "08" },
  // 競合状態（数量）
  competitorStatusToQuantity: { id: "09" },
  // 競合状態（単価）
  competitorStatusToPrice: { id: "10" }
}

/**
 * 見積チェックリスト（ラベル）
 */
export const EstimateCheckListCategory = {
  get equipmentType() { return i18n.t('common.constants.estimateCheckListCategory.equipmentType', { defaultValue: '設備種類' }); },
  get reverseAuction() { return i18n.t('common.constants.estimateCheckListCategory.reverseAuction', { defaultValue: 'リバースオークション有無' }); },
  get inHouseReview() { return i18n.t('common.constants.estimateCheckListCategory.inHouseReview', { defaultValue: '内製検討' }); },
  get inHouseReviewNoReason() { return i18n.t('common.constants.estimateCheckListCategory.inHouseReviewNoReason', { defaultValue: '内製検討なしの理由' }); },
  get competitiveEstimate() { return i18n.t('common.constants.estimateCheckListCategory.competitiveEstimate', { defaultValue: '相見積' }); },
  get competitiveEstimateNoReason() { return i18n.t('common.constants.estimateCheckListCategory.competitiveEstimateNoReason', { defaultValue: '相見積なしの理由' }); },
  get pastPerformance() { return i18n.t('common.constants.estimateCheckListCategory.pastPerformance', { defaultValue: '過去実績' }); },
  get purchaseDate() { return i18n.t('common.constants.estimateCheckListCategory.purchaseDate', { defaultValue: '直近購入時期' }); },
  get purchasePrice() { return i18n.t('common.constants.estimateCheckListCategory.purchasePrice', { defaultValue: '取得単価' }); },
  get supplier() { return i18n.t('common.constants.estimateCheckListCategory.supplier', { defaultValue: '発注先' }); },
  get orderDate() { return i18n.t('common.constants.estimateCheckListCategory.orderDate', { defaultValue: '発注時期' }); },
  get distributorReason() { return i18n.t('common.constants.estimateCheckListCategory.distributorReason', { defaultValue: '商社・代理店の理由' }); },
  get negotiation() { return i18n.t('common.constants.estimateCheckListCategory.negotiation', { defaultValue: '単価交渉の回数' }); },
  get negotiationBackground() { return i18n.t('common.constants.estimateCheckListCategory.negotiationBackground', { defaultValue: '単価交渉の経緯' }); },
  get paymentType() { return i18n.t('common.constants.estimateCheckListCategory.paymentType', { defaultValue: '支払条件' }); },
  get paymentBackground() { return i18n.t('common.constants.estimateCheckListCategory.paymentBackground', { defaultValue: '支払条件の経緯' }); },
  get ourProductUsage() { return i18n.t('common.constants.estimateCheckListCategory.ourProductUsage', { defaultValue: '当社製品使用の確認/要請' }); },
  get purchaseWithoutOutsourcing() { return i18n.t('common.constants.estimateCheckListCategory.purchaseWithoutOutsourcing', { defaultValue: '外注を使用せず購入する理由' }); },
  get equipmentComparison() { return i18n.t('common.constants.estimateCheckListCategory.equipmentComparison', { defaultValue: '相見積による比較' }); },
  get selectReason() { return i18n.t('common.constants.estimateCheckListCategory.selectReason', { defaultValue: '選定理由' }); },
}

/**
 * 設備種類（ラジオボタン）
 */
export const equipmentType = {

  genericEquipment: { value: "汎用設備", get name() { return i18n.t('common.constants.equipmentUsageType.2', { defaultValue: '汎用設備' }); } },
  dedicatedEquipment: { value: "専用設備", get name() { return i18n.t('common.constants.equipmentUsageType.1', { defaultValue: '専用設備' }); } },

};

/**
 * リバースオークション有無（ラジオボタン）
 */
export const reverseAuction = {

  isTrue: { value: "あり", get name() { return i18n.t('common.constants.yesNo.yes', { defaultValue: 'あり' }); } },
  isFalse: { value: "なし", get name() { return i18n.t('common.constants.yesNo.no', { defaultValue: 'なし' }); } },

};

/**
 * 内製検討（ラジオボタン）
 */
export const inHouseReview = {

  isTrue: { value: "あり", get name() { return i18n.t('common.constants.yesNo.yes', { defaultValue: 'あり' }); } },
  isFalse: { value: "なし", get name() { return i18n.t('common.constants.yesNo.no', { defaultValue: 'なし' }); } },

};

/**
 * 相見積（ラジオボタン）
 */
export const competitiveEstimate = {

  isTrue: { value: "あり", get name() { return i18n.t('common.constants.yesNo.yes', { defaultValue: 'あり' }); } },
  isFalse: { value: "なし", get name() { return i18n.t('common.constants.yesNo.no', { defaultValue: 'なし' }); } },

};

/**
 * 過去実績（ラジオボタン）
 */
export const pastPerformance = {

  isTrue: { value: "あり", get name() { return i18n.t('common.constants.yesNo.yes', { defaultValue: 'あり' }); } },
  isFalse: { value: "なし", get name() { return i18n.t('common.constants.yesNo.no', { defaultValue: 'なし' }); } },

};

/**
 * 発注先（ラジオボタン）
 */
export const supplier = {

  manufacturer: { value: "メーカー直販", get name() { return i18n.t('common.constants.supplierType.manufacturer', { defaultValue: 'メーカー直販' }); } },
  distributor: { value: "商社・代理店", get name() { return i18n.t('common.constants.supplierType.distributor', { defaultValue: '商社・代理店' }); } },

};

/**
 * 支払方法（ラジオボタン）
 */
export const paymentType = {

  isTrue: { value: "前払いあり", get name() { return i18n.t('common.constants.paymentType.yes', { defaultValue: '前払いあり' }); } },
  isFalse: { value: "前払いなし", get name() { return i18n.t('common.constants.paymentType.no', { defaultValue: '前払いなし' }); } },

};

/**
 * 調達物流部門担当役員の承認（ラジオボタン）
 */
export const procurementApproval = {

  isTrue: { value: "はい", get name() { return i18n.t('common.constants.procurementApproval.yes', { defaultValue: 'はい' }); } },
  isFalse: { value: "いいえ", get name() { return i18n.t('common.constants.procurementApproval.no', { defaultValue: 'いいえ' }); } },

};

/**
 * 当社製品使用の確認/要請（ラジオボタン）
 */
export const ourProductUsageStatus = {

  used: { value: "使用", get name() { return i18n.t('common.constants.ourProductUsage.used', { defaultValue: '使用' }); } },
  otherCompanyUsed: { value: "他社使用", get name() { return i18n.t('common.constants.ourProductUsage.otherCompanyUsed', { defaultValue: '他社使用' }); } },
  notApplicable: { value: "対象外", get name() { return i18n.t('common.constants.ourProductUsage.notApplicable', { defaultValue: '対象外' }); } },

};

/**
 * 相見積による比較（プルダウン）
 */
export const equipmentComparisonItems = {

  /** 精度/品質 */
  quality: {
    none: { value: "-", name: "-" },
    high: { value: "高", get name() { return i18n.t('common.constants.equipmentComparisonQuality.high', { defaultValue: '高' }); } },
    medium: { value: "中", get name() { return i18n.t('common.constants.equipmentComparisonQuality.medium', { defaultValue: '中' }); } },
    low: { value: "低", get name() { return i18n.t('common.constants.equipmentComparisonQuality.low', { defaultValue: '低' }); } },
  },
  /** サービス体制 */
  system: {
    none: { value: "-", name: "-" },
    isFalse: { value: "導入国 拠点なし", get name() { return i18n.t('common.constants.equipmentComparisonSystem.noBase', { defaultValue: '導入国 拠点なし' }); } },
    isTrue: { value: "導入国 拠点あり", get name() { return i18n.t('common.constants.equipmentComparisonSystem.hasBase', { defaultValue: '導入国 拠点あり' }); } },
  },
  /** リピート */
  repeat: {
    none: { value: "-", name: "-" },
    isFalse: { value: "量産実績なし", get name() { return i18n.t('common.constants.equipmentComparisonRepeat.noRecord', { defaultValue: '量産実績なし' }); } },
    isTrue: { value: "量産実績あり", get name() { return i18n.t('common.constants.equipmentComparisonRepeat.hasRecord', { defaultValue: '量産実績あり' }); } },
  },

};

/**
 * 見積チェックリスト Googleスプレッドシート連携
 */
export const EstimateCheckListSpreadSheet = {
  /** 保存先フォルダパス（マイドライブ） */
  folderPath: "CEAS/SpreadSheet/EstimateCheckList",
};

// 承認ステータスのコードから名称を取得するマッピング
export const StatusCodeToNameMap: Record<string, string> = Object.fromEntries(
  Object.values(ApproveStatus).map(({ code, name }) => [code, name])
);

// 承認結果のコードから名称を取得するマッピング
export const ApproveResultCodeToNameMap: Record<string, string> = Object.fromEntries(
  Object.values(ApproveResult).map(({ code, name }) => [code, name])
);

/** 顧客業界のコードから名称を取得するマッピング */
export const CustomerIndustryTypeCodeToNameMap: Record<string, string> = Object.fromEntries(
  Object.values(CustomerIndustry).map(({ code, name }) => [code, name])
);

/** 目的のコードから名称を取得するマッピング */
export const PurposeTypeCodeToNameMap: Record<string, string> = Object.fromEntries(
  Object.values(PurposeType).map(({ code, name }) => [code, name])
);

/** 設備区分のコードから名称を取得するマッピング */
export const EquipmentCategoryCodeToNameMap: Record<string, string> = Object.fromEntries(
  Object.values(EquipmentCategory).map(({ code, name }) => [code, name])
);

/** １０か条チェックリスト - 該当のコードから名称を取得するマッピング */
export const ApplicableCodeToNameMap: Record<string, string> = Object.fromEntries(
  Object.values(Applicable).map(({ code, name }) => [code, name])
);

/**
 * 指定した該当のコードから名称を取得するマッピング（汎用処理）
 * @param obj 該当のコードオブジェクト
 * @returns 名称マッピング
 */
export const codeToNameMap = (obj: { [key: string]: { code: string, name: string } }): Record<string, string> => Object.fromEntries(
  Object.values(obj).map(({ code, name }) => [code, name])
);

// 承認ステータスのコードから名称を取得
export const getStatusNameByCode = (code: string): string => {
  if (!code) return i18n.t("common.constants.unknown", { defaultValue: "不明" });
  const fallback = StatusCodeToNameMap[code];
  if (!fallback) return i18n.t("common.constants.unknown", { defaultValue: "不明" });
  return i18n.t(`common.constants.approveStatus.${code}`, { defaultValue: fallback });
};

// 承認結果のコードから名称を取得
export const getApproveResultNameByCode = (code: string): string => {
  if (!code) return "";
  const fallback = ApproveResultCodeToNameMap[code];
  if (!fallback) return "";
  return i18n.t(`common.constants.approveResult.${code}`, { defaultValue: fallback });
};

/** 顧客業界のコードから名称を取得 */
export const getCustomerIndustryTypeNameByCode = (code: string): string => {
  if (!code) return "";
  const fallback = CustomerIndustryTypeCodeToNameMap[code];
  if (!fallback) return "";
  return i18n.t(`common.constants.customerIndustry.${code}`, { defaultValue: fallback });
};

/** 目的のコードから名称を取得 */
export const getPurposeTypeNameByCode = (code: string): string => {
  if (!code) return "";
  const fallback = PurposeTypeCodeToNameMap[code];
  if (!fallback) return "";
  return i18n.t(`common.constants.purposeType.${code}`, { defaultValue: fallback });
};

/** 設備区分のコードから名称を取得 */
export const getEquipmentCategoryNameByCode = (code: string): string => {
  if (!code) return "";
  const fallback = EquipmentCategoryCodeToNameMap[code];
  if (!fallback) return "";
  return i18n.t(`common.constants.equipmentCategory.${code}`, { defaultValue: fallback });
};

/** 設備区分のコード配列から名称（読点区切り）を取得 */
export const getEquipmentCategoryNamesByCodes = (codes: string): string => {
  if (!codes) return "";
  return codes.split(",").map(code => getEquipmentCategoryNameByCode(code)).filter(name => name).join("、");
};

/** １０か条チェックリスト - 該当のコードから名称を取得 */
export const getApplicableNameByCode = (code: string): string => {
  if (code === undefined || code === null || code === "") return "";
  const fallback = ApplicableCodeToNameMap[code];
  // 空文字 fallback（Applicable.NotSelected 等）は i18next の returnEmptyString: false との
  // 組み合わせでキー名がそのまま返る恐れがあるため、早期 return で元挙動と一致させる
  if (fallback === undefined || fallback === "") return "";
  return i18n.t(`common.constants.applicable.${code}`, { defaultValue: fallback });
};

/** 設備用途区分のコードから名称を取得 */
export const getEquipmentUsageTypeNameByCode = (code: string): string => {
  if (!code) return "";
  const fallback = EquipmentUsageType.Dedicated.code === code ? EquipmentUsageType.Dedicated.name
    : EquipmentUsageType.GeneralPurpose.code === code ? EquipmentUsageType.GeneralPurpose.name : "";
  if (!fallback) return "";
  return i18n.t(`common.constants.equipmentUsageType.${code}`, { defaultValue: fallback });
};

/**
 * 計画予算区分のコード（または移行前の旧文字列）から名称を取得
 * ※ マイグレーション未実施の既存データ（"予算内"/"予算外"の生文字列）にも対応
 */
export const getPlannedBudgetTypeNameByCode = (codeOrLegacyValue: string): string => {
  if (!codeOrLegacyValue) return "";
  // 旧データ（生の日本語文字列）がそのまま来た場合はコードに正規化してから解決
  const normalizedCode =
    codeOrLegacyValue === "予算内" ? PlannedBudgetType.WithinBudget.code :
      codeOrLegacyValue === "予算外" ? PlannedBudgetType.OutsideBudget.code :
        codeOrLegacyValue;

  const fallback = PlannedBudgetType.WithinBudget.code === normalizedCode ? PlannedBudgetType.WithinBudget.name
    : PlannedBudgetType.OutsideBudget.code === normalizedCode ? PlannedBudgetType.OutsideBudget.name : "";
  if (!fallback) return "";
  return i18n.t(`common.constants.plannedBudgetType.${normalizedCode}`, { defaultValue: fallback });
};

/**
 * 指定した該当のコードから名称を取得（汎用処理）
 * @param obj 該当のコードオブジェクト
 * @param code コード
 * @returns コードに紐づく名称
 */
export const getNameByCode = (obj: { [key: string]: { code: string, name: string } }, code: string): string => {
  return codeToNameMap(obj)[code] || "";
};

/**
 * 指定したオブジェクトからmapを生成する。
 * @param obj オブジェクト
 * @returns map
 */
const createObjectTypeValueToNameMap = (obj: Record<string, any>) => {
  return Object.fromEntries(
    Object.values(obj).map(({ value, name }) => [value, name])
  );
};

/**
 * 指定した値に対応する名称
 * @param obj オブジェクト
 * @param value 値
 * @returns map
 */
const getObjectTypeNameByValue = (obj: any, value: string): string => {
  return createObjectTypeValueToNameMap(obj)[value] || "";
};

/**
 * 顧客取り決め事項 情報をフォーマットする。
 * @param pricingAgreementFormValue 顧客取り決め事項
 * @param materials 材料マスタ情報
 * @param Competitors 競合マスタ情報
 * @returns フォーマット文字列情報
 */
export const formatPricingAgreementForm = (
  pricingAgreementFormValue: PricingAgreementFormType,
  materials: Material[],
  Competitors: Competitor[],
): PricingAgreementFormFormatType => {
  // 単価の取り決め
  let pricing = getObjectTypeNameByValue(pricingType, pricingAgreementFormValue?.pricing.pricingType ?? "");
  if (pricingAgreementFormValue?.pricing.pricingType === pricingType.FixedPeriod.value) {
    pricing += i18n.t('common.constants.pricingAgreementForm.months', { count: pricingAgreementFormValue.pricing.pricingDuration ?? 0, defaultValue: `（${pricingAgreementFormValue.pricing.pricingDuration}ヶ月）` });
  }
  let pricingComment = pricingAgreementFormValue?.pricing?.pricingComment;
  // 為替変動の取り決め
  let exchange = getObjectTypeNameByValue(exchangeType, pricingAgreementFormValue?.exchange.exchangeType ?? "");
  if (pricingAgreementFormValue?.exchange.exchangeType === exchangeType.SubjectToFluctuations.value) {
    exchange += i18n.t('common.constants.pricingAgreementForm.exchangeFluctuation', {
      duration: pricingAgreementFormValue?.exchange.exchangeDuration,
      threshold: pricingAgreementFormValue?.exchange.exchangeThreshold,
      defaultValue: `（${pricingAgreementFormValue?.exchange.exchangeDuration}ヶ月に1度${pricingAgreementFormValue?.exchange.exchangeThreshold}%超）`,
    });
  }
  let exchangeComment = pricingAgreementFormValue?.exchange?.exchangeComment;
  // 材料費変動の取り決め
  let material = getObjectTypeNameByValue(materialType, pricingAgreementFormValue?.material.materialType ?? "");
  let selectMaterial = null;
  if (pricingAgreementFormValue?.material.materialType === materialType.SubjectToFluctuations.value) {
    // materials
    const materialNames = pricingAgreementFormValue.material.selectedMaterials.map((materialId: string) => {
      const material: any = materials.find((m: any) => m.materialId === materialId);
      return material?.materialName ? getMultilingualDisplayName(material.materialName) : undefined;
    }).filter((name: string | undefined): name is string => !!name);
    // その他材料がある場合は追加
    const otherMaterials = pricingAgreementFormValue.material?.otherMaterials;
    if (otherMaterials && otherMaterials.length > 0) {
      const otherMaterialNames = otherMaterials
        .map((m: any) => (typeof m === "string" ? m : getMultilingualDisplayName(m)))
        .filter((m: string) => m.trim() !== "");
      materialNames.push(...otherMaterialNames);
    } else {
      // 旧データ互換（otherMaterial単一文字列）：otherMaterialsが無い場合のみ
      const otherMaterial = (pricingAgreementFormValue.material as any)?.otherMaterial;
      if (otherMaterial && typeof otherMaterial === "string") {
        materialNames.push(otherMaterial);
      }
    }
    selectMaterial = `${materialNames.join(", ")}`;
  }
  let materialComment = pricingAgreementFormValue?.material?.materialComment;
  // 数量の取り決め
  let quantity = getObjectTypeNameByValue(quantityType, pricingAgreementFormValue?.quantity.quantityType ?? "");
  let notReachedComment = pricingAgreementFormValue?.quantity.notReachedComment;

  if (pricingAgreementFormValue?.quantity.quantityType === quantityType.Arrangement.value) {
    // 「取り決めあり」
    if (pricingAgreementFormValue?.quantity.quantitySubType === quantitySubType.Preliminary.value) {
      // 「内示」
      quantity += i18n.t('common.constants.pricingAgreementForm.quantityPreliminary', {
        count: pricingAgreementFormValue?.quantity.quantityDuration ?? 0,
        defaultValue: `（内示：${pricingAgreementFormValue?.quantity.quantityDuration}ヶ月）`,
      });
    } else if (pricingAgreementFormValue?.quantity.quantitySubType === quantitySubType.NoExpectation.value) {
      // 「取り決めなし」
      quantity += i18n.t('common.constants.pricingAgreementForm.quantityNoExpectation', {
        name: quantitySubType.NoExpectation.name,
        defaultValue: `（数量未達時の${quantitySubType.NoExpectation.name}）`,
      });
    }
  }
  if (!quantity) {
    quantity = i18n.t('common.constants.pricingAgreementForm.unknown', { defaultValue: '不明' });
  }
  let quantityComment = pricingAgreementFormValue?.quantity?.quantityComment;
  // 競合の有無
  let competitor = getObjectTypeNameByValue(competitorType, pricingAgreementFormValue?.competitor?.competitorType ?? "");
  let competitiorCondition = null;
  let selectCompetitor = null;
  let shareOfCompetitorAndCompany = pricingAgreementFormValue?.competitor?.shareOfCompetitorAndCompany;
  if (pricingAgreementFormValue?.competitor?.competitorType === competitorType.Competitor.value) {
    // competitors
    const competitorNames = pricingAgreementFormValue.competitor.selectedCompetitors.map((competitorId: string) => {
      if (competitorId === "OTHER") return pricingAgreementFormValue.competitor.competitorNameOther;
      if (competitorId === "UNCONFIRMED") return i18n.t('common.constants.pricingAgreementForm.competitorUnconfirmed', { defaultValue: '競合未確認' });
      const Competitor = Competitors.find((m: any) => m.competitorId === competitorId);
      return Competitor?.competitorName;
    }).filter((name): name is string => !!name);
    const sortedNames = competitorNames.sort((a, b) => a.localeCompare(b));
    selectCompetitor = `（${sortedNames.join(", ")}）`;

    // 競合状態
    // DBには日本語固定文言がenum識別子として保存されているため、
    // 表示時に選択言語へ変換する（保存形式・保存処理は変更しない）
    competitiorCondition = getObjectTypeNameByValue(
      competitiorConditionType,
      pricingAgreementFormValue?.competitor.competitiorCondition ?? "",
    ) || pricingAgreementFormValue?.competitor.competitiorCondition || null;
  }

  // 当社シェア
  let sharePercentage = pricingAgreementFormValue?.competitor?.sharePercentage ?? null;
  // 競合なしの場合は100%
  if (pricingAgreementFormValue?.competitor?.competitorType !== competitorType.Competitor.value) {
    sharePercentage = 100;
  }

  return {
    pricing: pricing,
    pricingComment: pricingComment,
    exchange: exchange,
    exchangeComment: exchangeComment,
    material: material,
    selectMaterial: selectMaterial,
    materialComment: materialComment,
    quantity: quantity,
    notReachedComment: notReachedComment,
    quantityComment: quantityComment,
    competitor: competitor,
    selectCompetitor: selectCompetitor,
    shareOfCompetitorAndCompany: shareOfCompetitorAndCompany,
    sharePercentage: sharePercentage,
    competitiorCondition,
    otherAgreements: pricingAgreementFormValue?.otherAgreements ?? [],
  };
}

/**
 * 年度別損益表の項目定義（レベルと名称を一括管理）
 * level: 1=大項目, 2=中項目, 3=小項目, 4=詳細項目
 */
export const ProfitAndLossItems = {
  totalSales: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.totalSales') } },
  productSales: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.productSales') } },
  moldRecovery: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.moldRecovery') } },
  devRecovery: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.devRecovery') } },
  equipmentRecovery: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.equipmentRecovery') } },
  salesCost: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.salesCost') } },
  materialCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.materialCost') } },
  outsideCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.outsideCost') } },
  laborCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.laborCost') } },
  domesticLaborCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticLaborCost') } },
  factoryExpenses: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.factoryExpenses') } },
  overHeadCost: { level: 3, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.overHeadCost') } },
  depreciation: { level: 3, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.depreciation') } },
  equipmentDepreciation: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.equipmentDepreciation') } },
  factoryCost: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.factoryCost') } },
  pastEquipmentCost: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.pastEquipmentCost') } },
  otherEquipmentCost: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.otherEquipmentCost') } },
  domesticFactoryExpenses: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticFactoryExpenses') } },
  domesticOverHeadCost: { level: 3, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticOverHeadCost') } },
  domesticDepreciation: { level: 3, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticDepreciation') } },
  domesticInvestmentDepreciation: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticInvestmentDepreciation') } },
  domesticFactoryBuildingDepreciation: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticFactoryBuildingDepreciation') } },
  domesticOtherDepreciation: { level: 4, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticOtherDepreciation') } },
  scrapCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.scrapCost') } },
  sgaExpenses: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.sgaExpenses') } },
  appliedLaborCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.appliedLaborCost') } },
  domesticSgaCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.domesticSgaCost') } },
  salesManagementCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.salesManagementCost') } },
  devCost: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.devCost') } },
  royalty: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.royalty') } },
  otherCost: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.otherCost') } },
  operatingProfit: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.operatingProfit') } },
  operatingProfitRate: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.operatingProfitRate') } },
  realOperatingProfitRate: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.realOperatingProfitRate') } },
};

/**
 * 回収期間（設備投資額ベース）の項目定義
 */
export const PaybackPeriodItems = {
  capexAmount: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.capexAmount') } },
  operatingProfit: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.operatingProfit') } },
  interestRate: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.interestRate') } },
  equipmentDepreciation: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.equipmentDepreciation') } },
  paybackPeriod: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.paybackPeriod') } },
};

/**
 * 回収シミュレーション（回収原資ベース）の項目定義
 */
export const RecoverySimulationItems = {
  capexAmount: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.capexAmount') } },
  recoveredResources: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.recoverySimulationItem.recoveredResources') } },
  operatingProfit: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.operatingProfit') } },
  equipmentDepreciation: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.equipmentDepreciation') } },
  uncollectedAmount: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.recoverySimulationItem.uncollectedAmount') } },
  collectionPeriod: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.recoverySimulationItem.collectionPeriod') } },
};

/**
 * ROICの項目定義
 */
export const ROICItems = {
  nopat: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.nopat') } },
  operatingProfit: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.operatingProfit') } },
  taxRate: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.taxRate') } },
  investedCapital: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.investedCapital') } },
  totalCapexAmount: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.totalCapexAmount') } },
  accumulatedDepreciation: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.accumulatedDepreciation') } },
  roic: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.roic') } },
  hurdleRate: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.hurdleRate') } },
};

/**
 * DCF法の項目定義
 */
export const DCFItems = {
  freeCashFlow: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.dcfItem.freeCashFlow') } },
  operatingProfit: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.operatingProfit') } },
  taxRate: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.roicItem.taxRate') } },
  depreciation: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.profitLossItem.depreciation') } },
  capexAmount: { level: 2, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.paybackPeriodItem.capexAmount') } },
  wacc: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.dcfItem.wacc') } },
  presentValue: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.dcfItem.presentValue') } },
  netPresentValue: { level: 1, get name() { return i18n.t('features.InvestmentScreening.ProjectProfitablityStatement.dcfItem.netPresentValue') } },
};

/** JIS規格 主要部門分類 */
export const JIS_DEPARTMENT_CLASSES = [
  { code: "A", get name() { return i18n.t('common.constants.jisDepartment.A', { defaultValue: '土木及び建築' }); } },
  { code: "B", get name() { return i18n.t('common.constants.jisDepartment.B', { defaultValue: '一般機械' }); } },
  { code: "C", get name() { return i18n.t('common.constants.jisDepartment.C', { defaultValue: '電子機器及び電気機械' }); } },
  { code: "D", get name() { return i18n.t('common.constants.jisDepartment.D', { defaultValue: '自動車' }); } },
  { code: "E", get name() { return i18n.t('common.constants.jisDepartment.E', { defaultValue: '鉄道' }); } },
  { code: "F", get name() { return i18n.t('common.constants.jisDepartment.F', { defaultValue: '船舶' }); } },
  { code: "G", get name() { return i18n.t('common.constants.jisDepartment.G', { defaultValue: '鉄鋼' }); } },
  { code: "H", get name() { return i18n.t('common.constants.jisDepartment.H', { defaultValue: '非鉄金属' }); } },
  { code: "K", get name() { return i18n.t('common.constants.jisDepartment.K', { defaultValue: '化学' }); } },
  { code: "L", get name() { return i18n.t('common.constants.jisDepartment.L', { defaultValue: '繊維' }); } },
  { code: "M", get name() { return i18n.t('common.constants.jisDepartment.M', { defaultValue: '鉱山' }); } },
  { code: "P", get name() { return i18n.t('common.constants.jisDepartment.P', { defaultValue: 'パルプ及び紙' }); } },
  { code: "R", get name() { return i18n.t('common.constants.jisDepartment.R', { defaultValue: '窯業' }); } },
  { code: "S", get name() { return i18n.t('common.constants.jisDepartment.S', { defaultValue: '日用品' }); } },
  { code: "T", get name() { return i18n.t('common.constants.jisDepartment.T', { defaultValue: '医療安全用具' }); } },
  { code: "W", get name() { return i18n.t('common.constants.jisDepartment.W', { defaultValue: '航空' }); } },
  { code: "X", get name() { return i18n.t('common.constants.jisDepartment.X', { defaultValue: '情報処理' }); } },
  { code: "Z", get name() { return i18n.t('common.constants.jisDepartment.Z', { defaultValue: 'その他' }); } },
];

/** 顧客スケジュール項目のプリセット候補 */
export const CustomerSchedulePresets = [
  { value: "RFQ（見積依頼）", get label() { return i18n.t('common.constants.customerSchedulePresets.rfq', { defaultValue: 'RFQ（見積依頼）' }); } },
  { value: "見積回答", get label() { return i18n.t('common.constants.customerSchedulePresets.quoteResponse', { defaultValue: '見積回答' }); } },
  { value: "受注確定", get label() { return i18n.t('common.constants.customerSchedulePresets.orderConfirmed', { defaultValue: '受注確定' }); } },
  { value: "サンプル納入", get label() { return i18n.t('common.constants.customerSchedulePresets.sampleDelivery', { defaultValue: 'サンプル納入' }); } },
  { value: "量産納入開始", get label() { return i18n.t('common.constants.customerSchedulePresets.massProductionStart', { defaultValue: '量産納入開始' }); } },
  { value: "顧客監査", get label() { return i18n.t('common.constants.customerSchedulePresets.customerAudit', { defaultValue: '顧客監査' }); } },
];

/** 設備投資スケジュール項目のプリセット候補 */
export const FacilityItemPresets = [
  { value: "PO（発注）", get label() { return i18n.t('common.constants.facilityItemPresets.po', { defaultValue: 'PO（発注）' }); } },
  { value: "SOP（量産開始）", get label() { return i18n.t('common.constants.facilityItemPresets.sop', { defaultValue: 'SOP（量産開始）' }); } },
  { value: "M/C Export（設備出荷）", get label() { return i18n.t('common.constants.facilityItemPresets.mcExport', { defaultValue: 'M/C Export（設備出荷）' }); } },
  { value: "M/C Setup（設備据付）", get label() { return i18n.t('common.constants.facilityItemPresets.mcSetup', { defaultValue: 'M/C Setup（設備据付）' }); } },
  { value: "MBO（設備検収）", get label() { return i18n.t('common.constants.facilityItemPresets.mbo', { defaultValue: 'MBO（設備検収）' }); } },
];

/** 振り返りフェーズのプリセット候補 */
export const ReviewPhasePresets = [
  { value: "初期確認", get label() { return i18n.t('common.constants.reviewPhasePresets.initial', { defaultValue: '初期確認（実行確認）' }); } },
  { value: "標準", get label() { return i18n.t('common.constants.reviewPhasePresets.standard', { defaultValue: '標準（予実管理）' }); } },
  { value: "中期", get label() { return i18n.t('common.constants.reviewPhasePresets.midTerm', { defaultValue: '中期（詳細検証）' }); } },
  { value: "最終", get label() { return i18n.t('common.constants.reviewPhasePresets.final', { defaultValue: '最終（効果確定）' }); } },
];
